import { SepaPaymentDTO } from '../types/validation';

export interface ParsedCsv {
  payments: SepaPaymentDTO[];
}

/**
 * Parse CSV text into SepaPaymentDTO array
 * CSV columns: EndToEndId, Amount, Currency, CreitorName, CreditorBIC, CreditorIBAN, DebtorName, DebtorBIC, DebtorIBAN, RemittanceInfo
 */
export function parseCsv(csvText: string): ParsedCsv {
  if (!csvText || typeof csvText !== 'string' || csvText.trim() === '') {
    throw new Error('CSV string is empty');
  }

  const lines = csvText.split('\n').map(line => line.trim()).filter(line => line.length > 0);
  
  if (lines.length === 0) {
    throw new Error('CSV file is empty');
  }

  // Detect separator from header row (supports both ',' and ';')
  const headerLine = lines[0];
  const separator = detectSeparator(headerLine);
  const headers = parseCsvLine(headerLine, separator).map(h => h.trim().toLowerCase());
                                                      
  const endToEndIdIndex = findColumnIndex(headers, ['endtoendid']);
  const amountIndex = findColumnIndex(headers, ['amount']);
  const currencyIndex = findColumnIndex(headers, ['currency']);
  const creditorNameIndex = findColumnIndex(headers, ['creitorname', 'creditorname']);
  const creditorIbanIndex = findColumnIndex(headers, ['creditoriban']);
  const debtorNameIndex = findColumnIndex(headers, ['debtorname']);
  const debtorIbanIndex = findColumnIndex(headers, ['debtoriban']);
  const remittanceInfoIndex = findColumnIndex(headers, ['remittanceinfo', 'remittanceinformation']);

  // Validate required columns
  if (endToEndIdIndex === -1) {
    throw new Error('CSV missing required column: EndToEndId');
  }
  if (amountIndex === -1) {
    throw new Error('CSV missing required column: Amount');
  }
  if (creditorIbanIndex === -1) {
    throw new Error('CSV missing required column: CreditorIBAN');
  }
  if (debtorIbanIndex === -1) {
    throw new Error('CSV missing required column: DebtorIBAN');
  }

  // Parse data rows
  const payments: SepaPaymentDTO[] = [];
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue; // Skip empty lines
    
    try {
      const values = parseCsvLine(line, separator);
      
      const endToEndId = values[endToEndIdIndex]?.trim() || '';
      const amountStr = values[amountIndex]?.trim() || '0';
      const currency = values[currencyIndex]?.trim() || 'EUR';
      const creditorName = values[creditorNameIndex]?.trim() || '';
      const creditorIban = values[creditorIbanIndex]?.trim() || '';
      const debtorName = values[debtorNameIndex]?.trim() || '';
      const debtorIban = values[debtorIbanIndex]?.trim() || '';
      const remittanceInfo = values[remittanceInfoIndex]?.trim() || '';

      // Validate required fields
      if (!endToEndId) {
        throw new Error(`Row ${i + 1}: EndToEndId is required`);
      }
      
      if (!creditorIban) {
        throw new Error(`Row ${i + 1}: CreditorIBAN is required`);
      }
      
      if (!debtorIban) {
        throw new Error(`Row ${i + 1}: DebtorIBAN is required`);
      }

      // Parse amount
      const amount = parseFloat(amountStr);
      if (isNaN(amount)) {
        throw new Error(`Row ${i + 1}: Invalid amount "${amountStr}"`);
      }

      const payment: SepaPaymentDTO = {
        paymentId: endToEndId,
        endToEndId: endToEndId,
        amount: amount,
        currency: currency,
        creditorName: creditorName,
        creditorIban: creditorIban,
        debtorName: debtorName,
        debtorIban: debtorIban,
        remittanceInformation: remittanceInfo,
        type: 'SCT' // Default to SEPA Credit Transfer
      };

      payments.push(payment);
    } catch (error) {
      throw new Error(`Error parsing CSV row ${i + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  if (payments.length === 0) {
    throw new Error('CSV file contains no valid payment records');
  }

  return { payments };
}

/**
 * Detect CSV separator (',' or ';') from header line
 */
function detectSeparator(headerLine: string): ',' | ';' {
  // Count occurrences of each separator (outside quotes)
  let commaCount = 0;
  let semicolonCount = 0;
  let inQuotes = false;
  
  for (let i = 0; i < headerLine.length; i++) {
    const char = headerLine[i];
    const nextChar = headerLine[i + 1];
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (!inQuotes) {
      if (char === ',') {
        commaCount++;
      } else if (char === ';') {
        semicolonCount++;
      }
    }
  }
  
  // Use semicolon if it appears more frequently, otherwise default to comma
  return semicolonCount > commaCount ? ';' : ',';
}

/**
 * Parse a CSV line handling quoted fields and supporting both ',' and ';' separators
 */
function parseCsvLine(line: string, separator: ',' | ';'): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === separator && !inQuotes) {
      // End of field (using detected separator)
      values.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  // Add last field
  values.push(current);
  
  return values;
}

/**
 * Find column index by matching header names (case-insensitive)
 */
function findColumnIndex(headers: string[], possibleNames: string[]): number {
  for (const name of possibleNames) {
    const index = headers.indexOf(name.toLowerCase());
    if (index !== -1) {
      return index;
    }
  }
  return -1;
}

